import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertTriangle, XCircle, FileX, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'VOID';

interface InvoiceStatusBadgeProps {
  status: string;
  className?: string;
}

const statusConfig = {
  DRAFT: {
    label: 'Issued',
    labelGR: 'Εκδομένο',
    variant: 'secondary' as const,
    icon: FileX,
    color: 'text-muted-foreground'
  },
  SENT: {
    label: 'Issued',
    labelGR: 'Εκδομένο', 
    variant: 'default' as const,
    icon: Send,
    color: 'text-blue-600'
  },
  PARTIALLY_PAID: {
    label: 'Partially Paid',
    labelGR: 'Μερικώς Εξοφλημένο',
    variant: 'default' as const,
    icon: Clock,
    color: 'text-orange-600'
  },
  PAID: {
    label: 'Paid',
    labelGR: 'Εξοφλημένο',
    variant: 'default' as const,
    icon: CheckCircle,
    color: 'text-green-600'
  },
  OVERDUE: {
    label: 'Overdue',
    labelGR: 'Εκπρόθεσμο',
    variant: 'destructive' as const,
    icon: AlertTriangle,
    color: 'text-red-600'
  },
  VOID: {
    label: 'Void',
    labelGR: 'Ακυρωμένο',
    variant: 'outline' as const,
    icon: XCircle,
    color: 'text-gray-500'
  }
};

export const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  className
}) => {
  // Don't show badge for 'sent' (issued) or 'draft' statuses
  const s = status?.toLowerCase();
  if (s === 'sent' || s === 'draft') {
    return null;
  }

  const key = (status || 'DRAFT').toString().toUpperCase() as InvoiceStatus;
  const config = statusConfig[key] || statusConfig.DRAFT;
  const Icon = config.icon;

  return (
    <Badge 
      variant={config.variant}
      className={cn('flex items-center gap-1.5', className)}
    >
      <Icon className={cn('h-3 w-3', config.color)} />
      <span>{config.label}</span>
    </Badge>
  );
};